import React, { useState, useRef } from 'react';
import {
  Box,
  TextField,
  IconButton,
  CircularProgress,
  LinearProgress,
  Typography,
} from '@mui/material';
import {
  Send as SendIcon,
  Image as ImageIcon,
  Gif as GifIcon,
  EmojiEmotions as EmojiIcon,
  ThumbUp as ThumbUpIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import GifPicker from './GifPicker';
import CustomEmojiPicker from './CustomEmojiPicker';

/**
 * Shared message input component used by both Messages page and ChatPopup
 * Handles text input, image uploads, GIF picker, emoji picker, and quick reactions
 */
const MessageInput = ({
  messageInput,
  onMessageChange,
  onSendMessage,
  onSendImage,
  onSendGif,
  onSendQuickReaction,
  sending = false,
  disabled = false,
  quickReactionEmoji = '👍',
  placeholder = 'Type a message...',
  compact = false, // For smaller layouts like popup
}) => {
  const [selectedImages, setSelectedImages] = useState([]);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isHoldingEmoji, setIsHoldingEmoji] = useState(false);
  const [emojiHoldProgress, setEmojiHoldProgress] = useState(0);
  const gifButtonRef = useRef(null);
  const emojiButtonRef = useRef(null);
  
  const fileInputRef = useRef(null);
  const emojiAnimationFrameRef = useRef(null);
  
  const EMOJI_HOLD_DURATION = 1000; // 1 second

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = async () => {
    if (!messageInput.trim() && selectedImages.length === 0) return;

    try {
      // Send images first
      if (selectedImages.length > 0 && onSendImage) {
        for (const image of selectedImages) {
          await onSendImage(image);
        }
        setSelectedImages([]);
      }

      // Send text message
      if (messageInput.trim() && onSendMessage) {
        await onSendMessage(messageInput);
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    setSelectedImages(prev => [...prev, ...imageFiles]);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleGifSelect = async (gifUrl) => {
    setShowGifPicker(false);
    if (onSendGif) {
      await onSendGif(gifUrl);
    }
  };

  const handleEmojiSelect = (emoji) => {
    if (onMessageChange) {
      onMessageChange({ target: { value: messageInput + emoji } });
    }
    setShowEmojiPicker(false);
  };

  const handleEmojiHoldStart = () => {
    setIsHoldingEmoji(true);
    setEmojiHoldProgress(0);
    
    const startTime = Date.now();
    
    const updateProgress = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min((elapsed / EMOJI_HOLD_DURATION) * 100, 100);
      setEmojiHoldProgress(progress);
      
      if (progress < 100) {
        emojiAnimationFrameRef.current = requestAnimationFrame(updateProgress);
      } else {
        handleSendQuickReaction();
      }
    };
    
    emojiAnimationFrameRef.current = requestAnimationFrame(updateProgress);
  };

  const handleEmojiHoldEnd = () => {
    setIsHoldingEmoji(false);
    setEmojiHoldProgress(0);
    
    if (emojiAnimationFrameRef.current) {
      cancelAnimationFrame(emojiAnimationFrameRef.current);
    }
  };

  const handleSendQuickReaction = async () => {
    if (onSendQuickReaction) {
      await onSendQuickReaction(quickReactionEmoji);
    }
    setIsHoldingEmoji(false);
    setEmojiHoldProgress(0);
  };

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (emojiAnimationFrameRef.current) {
        cancelAnimationFrame(emojiAnimationFrameRef.current);
      }
    };
  }, []);

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={handleImageSelect}
      />

      {/* GIF Picker */}
      {showGifPicker && (
        <>
          {console.log('GifPicker anchorEl:', gifButtonRef.current)}
          <GifPicker
            open={showGifPicker}
            onGifSelect={handleGifSelect}
            onClose={() => setShowGifPicker(false)}
            anchorEl={gifButtonRef.current}
          />
        </>
      )}

      {/* Emoji Picker */}
      {showEmojiPicker && (
        <CustomEmojiPicker
          open={showEmojiPicker}
          onEmojiSelect={handleEmojiSelect}
          onClose={() => setShowEmojiPicker(false)}
          anchorEl={emojiButtonRef.current}
        />
      )}

      {/* Image Preview */}
      {selectedImages.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            p: 1,
            overflowX: 'auto',
            borderTop: '1px solid #e0e0e0',
          }}
        >
          {selectedImages.map((image, index) => (
            <Box
              key={index}
              sx={{
                position: 'relative',
                width: compact ? 60 : 80,
                height: compact ? 60 : 80,
                flexShrink: 0,
              }}
            >
              <img
                src={URL.createObjectURL(image)}
                alt={`Preview ${index + 1}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: 8,
                }}
              />
              <IconButton
                size="small"
                onClick={() => handleRemoveImage(index)}
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  backgroundColor: 'rgba(0,0,0,0.6)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.8)',
                  },
                  width: 20,
                  height: 20,
                }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {/* Input Area */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 0.5,
          p: compact ? 1 : 1.5,
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#fff',
        }}
      >
        {/* Action Buttons */}
        <IconButton
          size="small"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
          sx={{ color: 'primary.main' }}
        >
          <ImageIcon fontSize="small" />
        </IconButton>
        
        <IconButton
          ref={gifButtonRef}
          size="small"
          onClick={() => {
    console.log("Click");
    setShowGifPicker(!showGifPicker);
  }}
          disabled={disabled}
          sx={{ color: 'primary.main' }}
        >
          <GifIcon fontSize="small" />
        </IconButton>
        
        <IconButton
          ref={emojiButtonRef}
          size="small"
          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
          disabled={disabled}
          sx={{ color: 'primary.main' }}
        >
          <EmojiIcon fontSize="small" />
        </IconButton>

        {/* Text Input */}
        <TextField
          fullWidth
          multiline
          maxRows={compact ? 3 : 4}
          size="small"
          placeholder={placeholder}
          value={messageInput}
          onChange={onMessageChange}
          onKeyPress={handleKeyPress}
          disabled={disabled}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              fontSize: compact ? '0.875rem' : '0.9375rem',
            },
          }}
        />

        {/* Send Button or Quick Reaction */}
        {messageInput.trim() || selectedImages.length > 0 ? (
          <IconButton
            size="small"
            onClick={handleSend}
            disabled={disabled}
            sx={{
              color: 'primary.main',
              backgroundColor: 'primary.light',
              '&:hover': {
                backgroundColor: 'primary.main',
                color: 'white',
              },
            }}
          >
            <SendIcon fontSize="small" />
          </IconButton>
        ) : (
          <Box sx={{ position: 'relative' }}>
            <IconButton
              size="small"
              onMouseDown={handleEmojiHoldStart}
              onMouseUp={handleEmojiHoldEnd}
              onMouseLeave={handleEmojiHoldEnd}
              onTouchStart={handleEmojiHoldStart}
              onTouchEnd={handleEmojiHoldEnd}
              disabled={disabled}
              sx={{
                color: 'primary.main',
                transform: isHoldingEmoji ? 'scale(1.1)' : 'scale(1)',
                transition: 'transform 0.2s',
                position: 'relative',
                '&::before': isHoldingEmoji ? {
                  content: '""',
                  position: 'absolute',
                  top: -2,
                  left: -2,
                  right: -2,
                  bottom: -2,
                  borderRadius: '50%',
                  border: '2px solid',
                  borderColor: 'primary.main',
                  borderTopColor: 'transparent',
                  animation: 'spin 1s linear infinite',
                } : {},
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
              title={`Hold to send ${quickReactionEmoji || '👍'}`}
            >
              {quickReactionEmoji ? (
                <Typography sx={{ fontSize: 18, lineHeight: 1 }}>{quickReactionEmoji}</Typography>
              ) : (
                <ThumbUpIcon fontSize="small" />
              )}
            </IconButton>
            {isHoldingEmoji && (
              <LinearProgress
                variant="determinate"
                value={emojiHoldProgress}
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 2,
                  borderRadius: 1,
                }}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MessageInput;
